import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Receipt, TrendingUp, Calendar, AlertTriangle, Building2, Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import DailyBriefingBar from '../components/DailyBriefingBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_COLORS = {
  'GoDaddy': '#1BDBDB',
  'SiteGround': '#42B983',
  'Shopify': '#95BF47',
  'Emergent': '#D4AF37',
  'Other': '#8B7355'
};

const BudgetManagement = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [newSub, setNewSub] = useState({
    company_name: '', service: '', type: '', description: '',
    cost_monthly: 0, cost_annual: 0, currency: 'GBP', status: 'active',
    renewal_date: '', auto_renew: true, category: 'hosting'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [subsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/budget/subscriptions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/budget/summary`, { withCredentials: true })
      ]);
      setSubscriptions(subsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching budget data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSubscription = async () => {
    try {
      await axios.post(`${API_URL}/api/budget/subscriptions`, newSub, { withCredentials: true });
      setShowAddForm(false);
      setNewSub({ company_name: '', service: '', type: '', description: '', cost_monthly: 0, cost_annual: 0, currency: 'GBP', status: 'active', renewal_date: '', auto_renew: true, category: 'hosting' });
      fetchData();
    } catch (error) {
      console.error('Error adding subscription:', error);
    }
  };

  const deleteSubscription = async (id) => {
    if (!window.confirm('Remove this subscription?')) return;
    try {
      await axios.delete(`${API_URL}/api/budget/subscriptions/${id}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error deleting subscription:', error);
    }
  };

  const startEdit = (sub) => {
    setEditingId(sub.id);
    setEditData({ cost_monthly: sub.cost_monthly, cost_annual: sub.cost_annual, status: sub.status, renewal_date: sub.renewal_date || '' });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API_URL}/api/budget/subscriptions/${id}`, editData, { withCredentials: true });
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
  };

  // Chart data
  const companyChartData = summary ? Object.entries(summary.by_company).map(([name, data]) => ({
    name: name.length > 12 ? name.substring(0, 12) + '...' : name,
    monthly: data.monthly,
    annual: data.annual
  })) : [];

  const serviceChartData = summary ? Object.entries(summary.by_service).map(([name, data]) => ({
    name,
    value: data.monthly,
    color: SERVICE_COLORS[name] || '#8B7355'
  })) : [];

  // Group subscriptions by company
  const subsByCompany = subscriptions.reduce((acc, sub) => {
    const name = sub.company_name;
    if (!acc[name]) acc[name] = [];
    acc[name].push(sub);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="budget-management">
      <DailyBriefingBar />

      <header className="bg-white" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 mb-4 transition-all duration-200" style={{ color: '#8B7355' }} data-testid="back-to-dashboard-button">
            <ArrowLeft className="w-5 h-5" /><span>Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#2C2416' }}>
                <Receipt className="w-6 h-6" style={{ color: '#D4AF37' }} />
              </div>
              <div>
                <h1 className="text-3xl font-light tracking-tight" style={{ color: '#2C2416' }}>Tech & Subscription Outgoings</h1>
                <p className="text-sm mt-1" style={{ color: '#8B7355' }}>Tech infrastructure costs across all companies</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm font-medium transition-all duration-200 hover:shadow-md"
              style={{ backgroundColor: '#D4AF37', color: 'white' }}
              data-testid="add-subscription-button"
            >
              <Plus className="w-4 h-4" /> Add Subscription
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-8">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="budget-summary">
            <div className="bg-white border rounded-sm shadow-sm p-5" style={{ borderColor: '#E5E1DB' }}>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7355' }}>Monthly Burn</p>
              <p className="text-3xl font-light" style={{ color: '#2C2416' }}>
                <span className="text-lg" style={{ color: '#8B7355' }}>&pound;</span>{summary.total_monthly.toFixed(2)}
              </p>
              <p className="text-xs mt-1" style={{ color: '#8B7355' }}>across {summary.subscription_count} subscriptions</p>
            </div>
            <div className="bg-white border rounded-sm shadow-sm p-5" style={{ borderColor: '#E5E1DB' }}>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7355' }}>Annual Cost</p>
              <p className="text-3xl font-light" style={{ color: '#2C2416' }}>
                <span className="text-lg" style={{ color: '#8B7355' }}>&pound;</span>{summary.total_annual.toFixed(2)}
              </p>
              <p className="text-xs mt-1" style={{ color: '#8B7355' }}>total per year</p>
            </div>
            <div className="bg-white border rounded-sm shadow-sm p-5" style={{ borderColor: '#E5E1DB' }}>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7355' }}>Services</p>
              <p className="text-3xl font-light" style={{ color: '#2C2416' }}>{Object.keys(summary.by_service).length}</p>
              <p className="text-xs mt-1" style={{ color: '#8B7355' }}>providers tracked</p>
            </div>
            <div className="bg-white border rounded-sm shadow-sm p-5" style={{ borderColor: '#E5E1DB' }}>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7355' }}>Next Renewal</p>
              {summary.upcoming_renewals.length > 0 ? (
                <>
                  <p className="text-lg font-medium" style={{ color: '#2C2416' }}>{summary.upcoming_renewals[0].service}</p>
                  <p className="text-xs mt-1" style={{ color: '#C2410C' }}>
                    {new Date(summary.upcoming_renewals[0].renewal_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </p>
                </>
              ) : (
                <p className="text-sm" style={{ color: '#8B7355' }}>No upcoming</p>
              )}
            </div>
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border rounded-sm shadow-sm p-6" style={{ borderColor: '#E5E1DB' }}>
            <h3 className="text-lg font-light mb-4" style={{ color: '#2C2416' }}>Monthly Cost by Company</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={companyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E1DB" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(val) => `\u00a3${val.toFixed(2)}`} />
                <Bar dataKey="monthly" fill="#D4AF37" name="Monthly" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border rounded-sm shadow-sm p-6" style={{ borderColor: '#E5E1DB' }}>
            <h3 className="text-lg font-light mb-4" style={{ color: '#2C2416' }}>Cost by Service</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={serviceChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: \u00a3${value.toFixed(0)}`}>
                  {serviceChartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(val) => `\u00a3${val.toFixed(2)}/mo`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Add Subscription Form */}
        {showAddForm && (
          <div className="bg-white border rounded-sm shadow-sm p-6" style={{ borderColor: '#D4AF37' }} data-testid="add-subscription-form">
            <h3 className="text-lg font-light mb-4" style={{ color: '#2C2416' }}>Add Subscription</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: '#8B7355' }}>Company</label>
                <input type="text" value={newSub.company_name} onChange={(e) => setNewSub({ ...newSub, company_name: e.target.value })} className="w-full px-3 py-2 border rounded-sm text-sm" style={{ borderColor: '#E5E1DB' }} placeholder="e.g. Star Talent Group" data-testid="new-sub-company" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: '#8B7355' }}>Service</label>
                <input type="text" value={newSub.service} onChange={(e) => setNewSub({ ...newSub, service: e.target.value })} className="w-full px-3 py-2 border rounded-sm text-sm" style={{ borderColor: '#E5E1DB' }} placeholder="e.g. Shopify" data-testid="new-sub-service" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: '#8B7355' }}>Description</label>
                <input type="text" value={newSub.description} onChange={(e) => setNewSub({ ...newSub, description: e.target.value })} className="w-full px-3 py-2 border rounded-sm text-sm" style={{ borderColor: '#E5E1DB' }} placeholder="What's this for?" data-testid="new-sub-description" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: '#8B7355' }}>Monthly Cost (&pound;)</label>
                <input type="number" step="0.01" value={newSub.cost_monthly} onChange={(e) => setNewSub({ ...newSub, cost_monthly: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-sm text-sm" style={{ borderColor: '#E5E1DB' }} data-testid="new-sub-monthly" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: '#8B7355' }}>Annual Cost (&pound;)</label>
                <input type="number" step="0.01" value={newSub.cost_annual} onChange={(e) => setNewSub({ ...newSub, cost_annual: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-sm text-sm" style={{ borderColor: '#E5E1DB' }} data-testid="new-sub-annual" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: '#8B7355' }}>Renewal Date</label>
                <input type="date" value={newSub.renewal_date} onChange={(e) => setNewSub({ ...newSub, renewal_date: e.target.value })} className="w-full px-3 py-2 border rounded-sm text-sm" style={{ borderColor: '#E5E1DB' }} data-testid="new-sub-renewal" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={addSubscription} className="px-5 py-2 rounded-sm text-sm font-medium" style={{ backgroundColor: '#D4AF37', color: 'white' }} data-testid="save-new-sub">Save</button>
              <button onClick={() => setShowAddForm(false)} className="px-5 py-2 rounded-sm text-sm" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Subscriptions by Company */}
        {Object.entries(subsByCompany).map(([companyName, subs]) => {
          const companyMonthly = subs.reduce((sum, s) => sum + (s.cost_monthly || 0), 0);
          return (
            <div key={companyName} className="bg-white border rounded-sm shadow-sm overflow-hidden" style={{ borderColor: '#E5E1DB' }} data-testid={`budget-company-${companyName.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  <h3 className="font-medium" style={{ color: '#2C2416' }}>{companyName}</h3>
                </div>
                <span className="text-sm font-mono" style={{ color: '#2C2416' }}>&pound;{companyMonthly.toFixed(2)}/mo</span>
              </div>
              <div className="divide-y" style={{ borderColor: '#F5F1EB' }}>
                {subs.map((sub) => (
                  <div key={sub.id} className="px-6 py-3 flex items-center gap-4" data-testid={`sub-${sub.id}`}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: (SERVICE_COLORS[sub.service] || '#8B7355') + '20' }}>
                      <TrendingUp className="w-4 h-4" style={{ color: SERVICE_COLORS[sub.service] || '#8B7355' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: '#2C2416' }}>{sub.service}</p>
                      <p className="text-xs" style={{ color: '#8B7355' }}>{sub.description}</p>
                    </div>
                    {editingId === sub.id ? (
                      <div className="flex items-center gap-2">
                        <input type="number" step="0.01" value={editData.cost_monthly} onChange={(e) => setEditData({ ...editData, cost_monthly: parseFloat(e.target.value) || 0 })} className="w-20 px-2 py-1 border rounded-sm text-sm font-mono" style={{ borderColor: '#D4AF37' }} data-testid="edit-cost-input" />
                        <span className="text-xs" style={{ color: '#8B7355' }}>/mo</span>
                        <button onClick={() => saveEdit(sub.id)} className="p-1 rounded-sm" style={{ backgroundColor: '#D4AF37' }} data-testid="save-edit-btn">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="p-1 rounded-sm" style={{ backgroundColor: '#F3F4F6' }}>
                          <X className="w-3.5 h-3.5" style={{ color: '#6B7280' }} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="text-right">
                          <p className="text-sm font-mono" style={{ color: '#2C2416' }}>&pound;{(sub.cost_monthly || 0).toFixed(2)}/mo</p>
                          {sub.renewal_date && (
                            <p className="text-xs flex items-center gap-1 justify-end" style={{ color: '#8B7355' }}>
                              <Calendar className="w-3 h-3" />
                              {new Date(sub.renewal_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => startEdit(sub)} className="p-1.5 rounded-sm transition-opacity hover:opacity-80" data-testid={`edit-sub-${sub.id}`}>
                            <Pencil className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
                          </button>
                          <button onClick={() => deleteSubscription(sub.id)} className="p-1.5 rounded-sm transition-opacity hover:opacity-80" data-testid={`delete-sub-${sub.id}`}>
                            <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Upcoming Renewals */}
        {summary && summary.upcoming_renewals.length > 0 && (
          <div className="bg-white border rounded-sm shadow-sm p-6" style={{ borderColor: '#E5E1DB' }} data-testid="upcoming-renewals">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5" style={{ color: '#D97706' }} />
              <h3 className="text-lg font-light" style={{ color: '#2C2416' }}>Upcoming Renewals</h3>
            </div>
            <div className="space-y-3">
              {summary.upcoming_renewals.map((sub, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-sm" style={{ backgroundColor: '#FDFCFB', border: '1px solid #E5E1DB' }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#2C2416' }}>{sub.service} — {sub.company_name}</p>
                    <p className="text-xs" style={{ color: '#8B7355' }}>{sub.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm" style={{ color: '#2C2416' }}>&pound;{(sub.cost_annual || 0).toFixed(2)}/yr</p>
                    <p className="text-xs font-medium" style={{ color: '#C2410C' }}>
                      {new Date(sub.renewal_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BudgetManagement;
